// The invitation page (admin PR 3b): /invite#email=…&token=… from "You've been invited to
// CloudPhone". The link is read once and taken out of the address bar before anything
// else loads, checked, then the person chooses their password and is logged in (an admin
// goes on to set up two-step login). Every bad link gets the same message.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, acceptInvite, checkInvite, getMe, type AuthOptions, type InviteCheck, type LoginResult, type User } from '../api';
import { MIN_PASSWORD_LENGTH, newPasswordProblem, parseInviteFragment } from '../password';
import { Logo } from './icons';
import { PasswordField } from './PasswordField';
import { StrengthMeter } from './StrengthMeter';
import { BotCheckError, Turnstile, type BotCheckHandle } from './Turnstile';
import { TwoStepCode, TwoStepSetup } from './TwoStep';

export const INVITE_PATH = '/invite';
const BAD_LINK = 'This invitation link has expired or was already used. Ask your admin to send a new one.';

type Step =
  | { kind: 'checking' }
  | { kind: 'invalid'; message: string }
  | { kind: 'password'; info: InviteCheck }
  | { kind: 'two-step'; mode: 'code' | 'setup'; ticket: string; email: string };

const errorText = (err: unknown, fallback: string) => (err instanceof ApiError || err instanceof BotCheckError ? err.message : fallback);

export function AcceptInvite({
  options,
  onLoggedIn,
  onLeave,
}: {
  options: AuthOptions | null;
  onLoggedIn: (u: User) => void;
  /** to the login page (a bad link, or "Back to log in" after the two-step step ran out) */
  onLeave: (message?: string, email?: string) => void;
}) {
  // read once, in the first render, and take it out of the address bar at once
  const [link] = useState(() => {
    const l = parseInviteFragment(window.location.hash);
    if (window.location.hash) history.replaceState(history.state, '', window.location.pathname);
    return l;
  });
  const [step, setStep] = useState<Step>(link ? { kind: 'checking' } : { kind: 'invalid', message: BAD_LINK });
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const siteKey = options?.turnstileSiteKey ?? null;
  const bot = useRef<BotCheckHandle>(null);
  const started = useRef(false);
  // this browser may already be logged in as someone else (an admin testing the link): say so first
  const [me, setMe] = useState<User | null>(null);
  const already = me?.email ?? null;
  useEffect(() => {
    getMe().then(setMe, () => setMe(null));
  }, []);

  // wait for the options (the Cloudflare site key) before the first check; try anyway after 8 s
  const [optionsLate, setOptionsLate] = useState(false);
  useEffect(() => {
    if (options !== null) return;
    const t = setTimeout(() => setOptionsLate(true), 8000);
    return () => clearTimeout(t);
  }, [options]);
  useEffect(() => {
    if (!link || started.current || step.kind !== 'checking' || (options === null && !optionsLate)) return;
    started.current = true;
    (async () => checkInvite(link.email, link.token, await bot.current?.run()))().then(
      (info) => setStep({ kind: 'password', info }),
      (err) => setStep({ kind: 'invalid', message: errorText(err, 'Couldn’t check the invitation. Reload the page to try again.') }),
    );
  }, [link, step.kind, options, optionsLate]);

  function next(r: LoginResult, email: string) {
    if (r.kind === 'done') onLoggedIn(r.user);
    else if (r.kind === 'two-step') setStep({ kind: 'two-step', mode: r.mode, ticket: r.ticket, email });
    else onLeave(undefined, email); // not expected: an invitation never gives a temporary password
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || step.kind !== 'password' || !link) return;
    const problem = newPasswordProblem(password, repeat);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      next(await acceptInvite(link.email, link.token, password, await bot.current?.run()), step.info.email);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invite_invalid') setStep({ kind: 'invalid', message: err.message });
      else setError(errorText(err, 'Couldn’t save the password. Try again.'));
      setBusy(false);
    }
  }

  if (step.kind === 'two-step') {
    return (
      <main className="login">
        <div className="login-card">
          <Logo />
          {step.mode === 'setup' ? (
            <TwoStepSetup ticket={step.ticket} email={step.email} onDone={(u) => onLoggedIn(u)} onOver={(m) => onLeave(m, step.email)} />
          ) : (
            <TwoStepCode ticket={step.ticket} email={step.email} onDone={onLoggedIn} onOver={(m) => onLeave(m, step.email)} />
          )}
        </div>
      </main>
    );
  }

  if (step.kind === 'checking') {
    return (
      <main className="login">
        <div className="login-card" aria-busy="true">
          <Logo />
          <h1>Welcome to CloudPhone</h1>
          <p className="login-notice">Checking your invitation…</p>
          <Turnstile ref={bot} siteKey={siteKey} action="invite" onSolved={() => setError(null)} />
        </div>
      </main>
    );
  }

  if (step.kind === 'invalid') {
    return (
      <main className="login">
        <div className="login-card">
          <Logo />
          <h1>This link doesn’t work</h1>
          <p className="login-error" role="alert">
            {step.message}
          </p>
          {me ? (
            // this browser is still logged in: back into the app, not a login page (owner, session 22)
            <button className="btn btn-primary btn-block" type="button" onClick={() => onLoggedIn(me)}>
              Back to CloudPhone
            </button>
          ) : (
            <>
              <p className="login-help">Already chose your password? Log in. Forgot it? Use “Forgot password?” on the login page.</p>
              <button className="btn btn-primary btn-block" type="button" onClick={() => onLeave()}>
                Go to log in
              </button>
            </>
          )}
        </div>
      </main>
    );
  }

  const { info } = step;
  return (
    <main className="login">
      <form className="login-card" onSubmit={submit} noValidate>
        <Logo />
        <h1>{info.name ? `Welcome, ${info.name.split(/\s+/)[0]}` : 'Welcome to CloudPhone'}</h1>
        <p className="login-notice">
          Choose a password for <strong className="login-email">{info.email}</strong>. At least {MIN_PASSWORD_LENGTH} characters. Only you will know it.
          {info.admin ? ' Next you’ll set up two-step login with an authenticator app.' : ''}
        </p>
        {already && already.toLowerCase() !== info.email.toLowerCase() && (
          <p className="login-notice login-warn" role="status">
            This browser is logged in as <strong>{already}</strong>. Saving logs that account out here and logs you in as {info.email}. To keep it,
            open this link in a private window or another browser.
          </p>
        )}
        {/* lets a password manager save the new password for the right account */}
        <input type="email" name="email" autoComplete="username" value={info.email} readOnly hidden />
        <PasswordField label="New password" name="new-password" autoComplete="new-password" required autoFocus value={password} onChange={setPassword}>
          <StrengthMeter password={password} email={info.email} />
        </PasswordField>
        <PasswordField label="Type it again" name="repeat-password" autoComplete="new-password" required value={repeat} onChange={setRepeat} />
        <Turnstile ref={bot} siteKey={siteKey} action="invite" onSolved={() => setError(null)} />
        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}
        <button className="btn btn-primary btn-block" type="submit" disabled={busy || !password || !repeat}>
          {busy ? 'Saving…' : info.admin ? 'Save and continue' : 'Save and start'}
        </button>
      </form>
    </main>
  );
}
